import { z } from "zod";
import { nodeTypeValues, type NodeType } from "../../schemas/nodeTypeSchema";
import { compareText, fail, ok, type FieldKind } from "../kind";
import { scalarToText } from "./text";

/**
 * Des références à des nodes du canvas, par id de nodeData (comme les
 * mentions des documents) : la référence survit au déplacement du node, et
 * vise la même donnée qu'il soit carte sur le canvas ou ligne de Database.
 * L'existence des ids se vérifie à l'écriture, côté serveur.
 */
/** L'ancien nom du node `file`, encore présent dans des réglages de champ. */
const LEGACY_PDF_NODE_TYPE = "pdf";

export const nodeSettings = z.object({
  // Vide : tous les types.
  allowedTypes: z
    .array(z.enum([...nodeTypeValues, LEGACY_PDF_NODE_TYPE]))
    .default([])
    // L'ancien node `pdf` est devenu `file`. Les réglages des champs ne sont
    // pas validés par le schéma (`nodeTemplates.fields` est `v.any()`) : un
    // champ enregistré avant porte encore `"pdf"`, qu'on lit comme `"file"`.
    .transform((types): NodeType[] => [
      ...new Set(types.map((type) => (type === LEGACY_PDF_NODE_TYPE ? "file" : type))),
    ]),
  multiple: z.boolean().default(true),
  display: z.enum(["pill", "preview"]).default("pill"),
});

const MAX_REFS = 100;
const idSchema = z.string().min(1).max(64);

function title(id: string, resolve?: (id: string) => string | undefined): string {
  return resolve?.(id) ?? id;
}

export const nodeKind: FieldKind<"node"> = {
  type: "node",
  settings: nodeSettings,
  value: z.array(idSchema).min(1).max(MAX_REFS),
  coerce(input, settings) {
    const raw = Array.isArray(input)
      ? input
      : typeof input === "string"
        ? input.split(/[,;\s]+/)
        : [input];
    const ids: string[] = [];
    for (const item of raw) {
      const id = scalarToText(item)?.trim();
      if (id === undefined) return fail("Expected node ids.");
      if (id === "") continue;
      if (!idSchema.safeParse(id).success) return fail(`"${id}" is not a node id.`);
      if (!ids.includes(id)) ids.push(id);
    }
    if (ids.length === 0) return ok(null);
    if (!settings.multiple && ids.length > 1) return fail("Only one node can be referenced.");
    if (ids.length > MAX_REFS) return fail(`At most ${MAX_REFS} nodes.`);
    return ok(ids);
  },
  isEmpty: (value) => value.length === 0,
  // En `plain`, les ids : relisibles par `coerce`.
  format: (value, _settings, ctx) =>
    ctx.style === "plain"
      ? value.join(", ")
      : value.map((id) => title(id, ctx.resolveNodeTitle)).join(", "),
  compare: (a, b) => compareText(a[0] ?? "", b[0] ?? "") || a.length - b.length,
  filters: [
    { id: "is_any_of", operand: "values", test: (v, o: string[]) => v.some((id) => o.includes(id)) },
    {
      id: "is_none_of",
      operand: "values",
      matchesEmpty: true,
      test: (v, o: string[]) => !v.some((id) => o.includes(id)),
    },
  ],
  aggregates: ["count", "count_empty", "count_not_empty", "percent_empty", "percent_not_empty"],
  // Ce que l'agent écrit : l'id d'un node du canvas, traduit en nodeData à
  // l'écriture (cf. `agentFieldInput`).
};
